<script setup lang="ts">
  import { ref, toRef } from 'vue';
  import { useRouter } from 'vue-router';
  import type {
    MatrixCodeRevision,
    MatrixCodeRevisionsPage,
    MatrixFunctionalInspectionType,
  } from '@/entities/Matrix';
  import { QUANTITY_OPTIONS, SHOW_ALL_ITEMS_QUANTITY } from '@/shared/config';
  import {
    ColoredButton,
    FieldSearchBar,
    NoDocuments,
    Pagination,
    SearchToolbar,
    SelectInput,
  } from '@/shared/ui';
  import FilterIcon from '@/shared/assets/icons/Filter.svg';
  import OrderIcon from '@/shared/assets/icons/Order.svg';
  import {
    MATRIX_REVISIONS_RESTRICTED_DESCRIPTION,
    MATRIX_REVISIONS_RESTRICTED_TITLE,
  } from '../../config';
  import {
    resolveMatrixRevisionGenerateAction,
    resolveMatrixRevisionInspectionType,
  } from '../../model/matrix-revision-generate-action';
  import { useMatrixPageOrigin } from '../../model/useMatrixPageOrigin';
  import { useMatrixRevisionModals } from '../../model/useMatrixRevisionModals';
  import { useMatrixRevisionsTab } from '../../model/useMatrixRevisionsTab';
  import InspectionIdentifierModal from './InspectionIdentifierModal.vue';
  import MatrixRevisionActionsCell from './MatrixRevisionActionsCell.vue';
  import MatrixRevisionsTable from './MatrixRevisionsTable.vue';

  const props = withDefaults(
    defineProps<{
      prefix: string;
      initialPage?: MatrixCodeRevisionsPage | null;
      matrixId?: number | null;
    }>(),
    {
      initialPage: null,
      matrixId: null,
    }
  );

  const SEARCH_FIELDS = [
    { id: 'registro', name: 'Registro' },
    { id: 'titulo', name: 'Título' },
  ];

  // No "Todos os documentos": the server pages the list, and a "todos" capped at 100 would lie.
  const QUANTITY_PER_PAGE_OPTIONS = QUANTITY_OPTIONS.filter(
    (option) => option.id !== SHOW_ALL_ITEMS_QUANTITY
  );

  const searchFieldId = ref(SEARCH_FIELDS[0].id);

  const {
    page,
    itemsPerPage,
    rows,
    total,
    restricted,
    loading,
    error,
    retry,
    goToPage,
    changeItemsPerPage,
  } = useMatrixRevisionsTab(toRef(props, 'prefix'), props.initialPage, props.matrixId);

  const { openHistory, openControl } = useMatrixRevisionModals();

  const router = useRouter();
  const { returnQuery } = useMatrixPageOrigin();

  const identifying = ref<{
    matrixId: number;
    inspectionType: MatrixFunctionalInspectionType;
  } | null>(null);

  const goToGenerateRecord = (matrixId: number, identifier?: string) => {
    const query = {
      ...returnQuery().query,
      ...(identifier === undefined ? {} : { identifier }),
    };
    router.push({
      name: 'GerarRegistro',
      params: { prefix: props.prefix, matrixId: String(matrixId) },
      ...(Object.keys(query).length > 0 ? { query } : {}),
    });
  };

  const openGenerateRecord = (row: MatrixCodeRevision) => {
    const inspectionType = resolveMatrixRevisionInspectionType(row);
    if (inspectionType !== null) {
      identifying.value = { matrixId: row.id, inspectionType };
      return;
    }

    goToGenerateRecord(row.id);
  };

  const closeIdentification = () => {
    identifying.value = null;
  };

  const generateIdentifiedRecord = (identifier: string) => {
    const matrixId = identifying.value?.matrixId;
    identifying.value = null;
    if (matrixId !== undefined) goToGenerateRecord(matrixId, identifier);
  };
</script>

<template>
  <div
    class="flex w-full flex-col gap-4"
    data-testid="matrix-revisions-tab"
  >
    <div
      v-if="restricted"
      class="rounded-2xl border border-gray-02 bg-white-01 py-8"
      data-testid="matrix-revisions-restricted"
    >
      <NoDocuments
        :title="MATRIX_REVISIONS_RESTRICTED_TITLE"
        :description="MATRIX_REVISIONS_RESTRICTED_DESCRIPTION"
      />
    </div>

    <template v-else>
      <SearchToolbar class="mb-2">
        <template #search>
          <!-- Without `independent-search-field` the scope goes to the session-wide search store. -->
          <FieldSearchBar
            v-model:search-field-id="searchFieldId"
            :search-fields="SEARCH_FIELDS"
            independent-search-field
          />
        </template>
        <template #quantity>
          <SelectInput
            :model-value="itemsPerPage"
            :options="QUANTITY_PER_PAGE_OPTIONS"
            class="cursor-pointer"
            data-testid="matrix-revisions-quantity"
            @update:model-value="changeItemsPerPage"
          />
        </template>
      </SearchToolbar>

      <div>
        <MatrixRevisionsTable
          :rows="rows"
          :loading="loading"
          :error="error"
          @retry="retry"
        >
          <template #header>
            <div class="flex items-center justify-between gap-4">
              <span
                class="text-sm font-semibold text-gray-07"
                data-testid="matrix-revisions-title"
              >
                Matrizes
              </span>
              <div class="flex items-center gap-3">
                <ColoredButton
                  text="Ordenar por"
                  :icon="OrderIcon"
                  icon-alt="Ícone de ordenação"
                  outlined
                  data-testid="matrix-revisions-order"
                />
                <ColoredButton
                  text="Filtrar"
                  :icon="FilterIcon"
                  icon-alt="Ícone de filtro"
                  outlined
                  data-testid="matrix-revisions-filter"
                />
              </div>
            </div>
          </template>
          <template #actions="{ row }">
            <MatrixRevisionActionsCell
              :row="row"
              :generate-action="resolveMatrixRevisionGenerateAction(row)"
              @open-history="openHistory"
              @open-control="openControl"
              @generate="openGenerateRecord"
            />
          </template>
        </MatrixRevisionsTable>

        <div
          v-if="!error && total > 0"
          class="mt-3"
        >
          <Pagination
            :total-items="total"
            :current-page="page"
            :items-per-page="itemsPerPage"
            entity-name="matriz"
            @page-changed="goToPage"
          />
        </div>
      </div>
    </template>

    <InspectionIdentifierModal
      v-if="identifying"
      :matrix-id="identifying.matrixId"
      :inspection-type="identifying.inspectionType"
      @close="closeIdentification"
      @identified="generateIdentifiedRecord"
    />
  </div>
</template>
